<template>
  <div class="domain-detail-page">
    <!-- Background elements -->
    <div class="bg-grid"></div>
    <div class="bg-glow bg-glow-1"></div>

    <!-- Enable Loader Overlay -->
    <Transition name="fade-overlay">
      <div v-if="enabling" class="loader-overlay">
        <div class="loader-content">
          <div class="spinner-large"></div>
          <div class="loader-text">Enabling domain...</div>
        </div>
      </div>
    </Transition>

    <div class="detail-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner-large"></div>
        <p>Loading domain details...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <p>{{ error }}</p>
      </div>

      <div v-else-if="domain" class="domain-content">
        <!-- Header -->
        <div class="page-header">
          <div class="header-info">
            <h1>{{ domain.name || 'Domain Details' }}</h1>
            <p class="domain-url">{{ domain.url }}</p>
          </div>
          <div class="action-buttons">
            <template v-if="!isEditing">
              <button @click="startEditing" class="btn btn-primary">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Edit
              </button>
              <button v-if="!hasBeenScanned" @click="confirmDelete" class="btn btn-danger">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Delete
              </button>
            </template>
            <template v-else>
              <button @click="saveChanges" class="btn btn-success" :disabled="saving">
                <span v-if="saving" class="spinner"></span>
                <template v-else>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Save
                </template>
              </button>
              <button @click="cancelEditing" class="btn btn-secondary" :disabled="saving">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
                Cancel
              </button>
            </template>
          </div>
        </div>

        <!-- Domain Info Card -->
        <div class="info-card">
          <div class="card-section">
            <h3 class="section-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              General Information
            </h3>
            
            <div class="info-grid">
              <div class="info-item">
                <label>Name</label>
                <div class="value">
                  <input v-if="isEditing" v-model="editedDomain.name" type="text" class="form-input">
                  <span v-else>{{ domain.name }}</span>
                </div>
              </div>

              <div class="info-item">
                <label>URL</label>
                <div class="value">
                  <input v-if="isEditing" v-model="editedDomain.url" type="text" class="form-input">
                  <span v-else class="url-value">{{ domain.url }}</span>
                </div>
              </div>

              <div class="info-item">
                <label>Status</label>
                <div class="value">
                  <div 
                    v-if="!isEditing"
                    class="status-toggle"
                    :class="{ disabled: enabling }"
                    @click="toggleDomainStatus"
                  >
                    <div class="toggle-track">
                      <div 
                        class="toggle-slider"
                        :class="{ 'slider-enabled': domain.isEnabled, 'slider-disabled': !domain.isEnabled }"
                      ></div>
                      <div class="toggle-option" :class="{ active: domain.isEnabled }">
                        Enabled
                      </div>
                      <div class="toggle-option" :class="{ active: !domain.isEnabled }">
                        Disabled
                      </div>
                    </div>
                  </div>
                  <span 
                    v-else
                    :class="['status-badge', domain.isEnabled ? 'enabled' : 'disabled']"
                  >
                    {{ domain.isEnabled ? 'Enabled' : 'Disabled' }}
                  </span>
                </div>
              </div>

            </div>
          </div>

          <div class="card-section">
            <h3 class="section-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
                <line x1="6" y1="6" x2="6.01" y2="6"/>
                <line x1="6" y1="18" x2="6.01" y2="18"/>
              </svg>
              FTP Configuration
            </h3>
            
            <div class="info-grid">
              <div class="info-item">
                <label>FTP URL</label>
                <div class="value">
                  <input v-if="isEditing" v-model="editedDomain.ftpUrl" type="text" class="form-input">
                  <span v-else>{{ domain.ftpUrl || '—' }}</span>
                </div>
              </div>

              <div class="info-item">
                <label>Port</label>
                <div class="value">
                  <input v-if="isEditing" v-model="editedDomain.port" type="number" class="form-input">
                  <span v-else>{{ domain.port || '—' }}</span>
                </div>
              </div>

              <div class="info-item">
                <label>FTP Username</label>
                <div class="value">
                  <input v-if="isEditing" v-model="editedDomain.username" type="text" class="form-input">
                  <span v-else>{{ domain.username || '—' }}</span>
                </div>
              </div>

              <div class="info-item">
                <label>FTP Password</label>
                <div class="value">
                  <input v-if="isEditing" v-model="editedDomain.password" type="password" class="form-input" placeholder="Enter new password">
                  <span v-else class="masked-value">••••••••</span>
                </div>
              </div>

              <div class="info-item">
                <label>Initial Path</label>
                <div class="value">
                  <input v-if="isEditing" v-model="editedDomain.initPath" type="text" class="form-input">
                  <span v-else>{{ domain.initPath || '/' }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="card-section">
            <h3 class="section-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              Scan Settings
            </h3>
            
            <div class="info-grid">
              <div class="info-item full-width">
                <label>Exclude List</label>
                <div class="value">
                  <div v-if="isEditing">
                    <textarea v-model="editedDomain.excludeList" class="form-textarea" rows="3" placeholder="Enter paths to exclude, one per line"></textarea>
                  </div>
                  <div v-else>
                    <div v-if="domain.excludeList.length === 0" class="no-items">None</div>
                    <div v-else class="exclude-list-preview" @click="showExcludeListModal = true">
                      {{ getExcludeListPreview() }}
                      <span class="click-hint">(click to view full list)</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="info-item full-width">
                <label>Scan Extensions</label>
                <div class="value">
                  <div v-if="isEditing" class="chip-input-container">
                    <div class="chips-wrapper">
                      <div v-for="(ext, index) in editedDomain.scanExtensions" :key="index" class="chip">
                        {{ ext }}
                        <span class="chip-remove" @click="removeExtension(index)">&times;</span>
                      </div>
                      <input
                        v-model="newExtension"
                        @keydown.enter.prevent="addExtension"
                        type="text"
                        class="chip-input"
                        placeholder="jpg"
                      >
                    </div>
                  </div>
                  <div v-else>
                    <div v-if="domain.scanExtensions.length === 0" class="no-items">None</div>
                    <div v-else class="extensions-list">
                      <span v-for="ext in domain.scanExtensions" :key="ext" class="extension-tag">{{ ext }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card-section">
            <h3 class="section-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              Timestamps
            </h3>
            
            <div class="info-grid timestamps-grid">
              <div class="info-item">
                <label>Created At</label>
                <div class="value timestamp">{{ new Date(domain.createdAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'medium' }) }}</div>
              </div>

              <div class="info-item">
                <label>Updated At</label>
                <div class="value timestamp">{{ new Date(domain.updatedAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'medium' }) }}</div>
              </div>

              <div class="info-item">
                <label>Last Scan</label>
                <div class="value timestamp">{{ formatLastScanDate(domain.lastScanAt) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <Transition name="toast">
      <div v-if="toast.show" :class="['toast', toast.type]" @click="toast.show = false">
        <svg v-if="toast.type === 'success'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
        <svg v-else-if="toast.type === 'error'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        <span>{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- Exclude List Modal -->
    <Transition name="modal">
      <div v-if="showExcludeListModal" class="modal-overlay" @click="showExcludeListModal = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Exclude List</h3>
            <button class="close-button" @click="showExcludeListModal = false">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="domain.excludeList.length === 0" class="no-items">No items in exclude list</div>
            <div v-else class="exclude-list">
              <div v-for="(item, index) in domain.excludeList" :key="index" class="exclude-item">
                {{ item }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Delete Confirmation Modal -->
    <Transition name="modal">
      <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
        <div class="modal-content delete-modal" @click.stop>
          <div class="modal-header">
            <h3>Delete Domain</h3>
            <button class="close-button" @click="showDeleteModal = false">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <div class="delete-warning">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <p>Are you sure you want to delete the domain "<strong>{{ domain.name }}</strong>"?</p>
            <p class="delete-note">This action cannot be undone.</p>
            <div class="delete-actions">
              <button @click="showDeleteModal = false" class="btn btn-secondary" :disabled="deleting">
                Cancel
              </button>
              <button @click="deleteDomain" class="btn btn-danger" :disabled="deleting">
                <span v-if="deleting" class="spinner"></span>
                <span v-else>Delete Domain</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { useRouter } from 'vue-router'
import { inject } from 'vue'
import { api } from '../utils/api'

export default {
  name: 'DomainDetail',
  setup() {
    const router = useRouter()
    const env = inject('env')
    return { router, env }
  },
  data() {
    return {
      domain: null,
      editedDomain: null,
      loading: true,
      error: null,
      showExcludeListModal: false,
      showDeleteModal: false,
      isEditing: false,
      saving: false,
      enabling: false,
      deleting: false,
      newExtension: '',
      toast: {
        show: false,
        message: '',
        type: 'error'
      }
    }
  },
  computed: {
    hasBeenScanned() {
      if (!this.domain || !this.domain.lastScanAt) return false
      return this.domain.lastScanAt !== "0001-01-01T00:00:00Z"
    }
  },
  async created() {
    await this.fetchDomain()
  },
  methods: {
    async fetchDomain() {
      try {
        this.domain = await api(`/domains/${this.$route.params.id}`, {}, this.$router)
      } catch (error) {
        this.error = error.code || 'Something went wrong please contact support'
      } finally {
        this.loading = false
      }
    },
    getExcludeListPreview() {
      if (!this.domain.excludeList.length) return 'None'
      const preview = this.domain.excludeList.join('\n')
      return preview.length > 50 ? preview.substring(0, 50) + '...' : preview
    },
    startEditing() {
      this.editedDomain = JSON.parse(JSON.stringify(this.domain))
      this.editedDomain.excludeList = this.editedDomain.excludeList.join('\n')
      this.isEditing = true
    },
    addExtension() {
      const ext = this.newExtension.trim()
      if (ext && !this.editedDomain.scanExtensions.includes(ext)) {
        this.editedDomain.scanExtensions.push(ext)
        this.newExtension = ''
      }
    },
    removeExtension(index) {
      this.editedDomain.scanExtensions.splice(index, 1)
    },
    cancelEditing() {
      this.editedDomain = null
      this.isEditing = false
    },
    showToast(message, type = 'error') {
      this.toast = {
        show: true,
        message,
        type
      }
      setTimeout(() => {
        this.toast.show = false
      }, 12000)
    },
    formatLastScanDate(lastScanAt) {
      if (lastScanAt === "0001-01-01T00:00:00Z") {
        return 'N/A'
      }
      return new Date(lastScanAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'medium' })
    },
    async saveChanges() {
      this.saving = true
      const enableValue = true
      try {
        const domainToSave = {
          ...this.editedDomain,
          excludeList: this.editedDomain.excludeList
            .split('\n')
            .map(item => item.trim())
            .filter(item => item.length > 0)
        }

        await api(`/domains/${this.$route.params.id}`, {
          method: 'PUT',
          body: JSON.stringify(domainToSave)
        }, this.$router)

        this.enabling = true
        try {
          await api(`/domains/${this.$route.params.id}/enable`, {
            method: 'POST',
            body: JSON.stringify({ enable: enableValue })
          }, this.$router)
          
          this.showToast('Domain updated successfully', 'success')
          await this.fetchDomain()
        } catch (error) {
          if (error.message.includes('json')) {
            this.showToast('Domain updated and enabled successfully', 'success')
            await this.fetchDomain()
          } else {
            const errorMessage = error.code || error.message
            this.showToast(`Domain saved but could not be enabled:\n${errorMessage}`, 'warning')
            await this.fetchDomain()
          }
        } finally {
          this.enabling = false
        }

        this.isEditing = false
        this.editedDomain = null
      } catch (error) {
        const errorMessage = error.code || 'Something went wrong please contact support'
        this.showToast(errorMessage)
      } finally {
        this.saving = false
      }
    },
    async toggleDomainStatus() {
      if (this.enabling) return
      
      this.enabling = true
      try {
        const newEnableState = !this.domain.isEnabled
        await api(`/domains/${this.$route.params.id}/enable`, {
          method: 'POST',
          body: JSON.stringify({ enable: newEnableState })
        }, this.$router)
        
        this.showToast(`Domain ${newEnableState ? 'enabled' : 'disabled'} successfully`, 'success')
        await this.fetchDomain()
      } catch (error) {
        const errorMessage = error.code || 'Something went wrong please contact support'
        this.showToast(`Failed to ${this.domain.isEnabled ? 'disable' : 'enable'} domain: ${errorMessage}`)
      } finally {
        this.enabling = false
      }
    },
    confirmDelete() {
      this.showDeleteModal = true
    },
    async deleteDomain() {
      this.deleting = true
      try {
        await api(`/domains/${this.$route.params.id}`, {
          method: 'DELETE'
        }, this.$router)
        
        this.$router.push({ path: '/domains', query: { deleted: '1' } })
      } catch (error) {
        const errorMessage = error.code || 'Something went wrong please contact support'
        this.showToast(errorMessage)
        this.showDeleteModal = false
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>

<style scoped>
.domain-detail-page {
  min-height: calc(100vh - 40px);
  position: relative;
  padding: 32px 24px;
}

/* Background elements */
.bg-grid {
  position: fixed;
  inset: 0;
  background-image: 
    linear-gradient(rgba(60, 98, 230, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(60, 98, 230, 0.02) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.25;
  pointer-events: none;
  z-index: 0;
}

.bg-glow-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(60, 98, 230, 0.2) 0%, transparent 70%);
  top: -150px;
  right: 5%;
}

.detail-container {
  position: relative;
  z-index: 1;
  max-width: 900px;
  margin: 0 auto;
  animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Loading & Error states */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-secondary);
}

.loading-state p,
.error-state p {
  margin-top: 16px;
  font-size: 1rem;
}

.error-state svg {
  width: 48px;
  height: 48px;
  color: var(--error-color);
}

.error-state p {
  color: var(--error-color);
}

/* Page header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 20px;
  flex-wrap: wrap;
}

.header-info h1 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.domain-url {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0;
}

.action-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* Info Card */
.info-card {
  background: rgba(24, 24, 25, 0.7);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  overflow: hidden;
}

.card-section {
  padding: 24px 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.card-section:last-child {
  border-bottom: none;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 20px 0;
}

.section-title svg {
  width: 18px;
  height: 18px;
  color: var(--accent-color);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.timestamps-grid {
  grid-template-columns: repeat(3, 1fr);
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item label {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.info-item .value {
  color: var(--text-primary);
  font-size: 0.95rem;
}

.info-item .value.timestamp {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.url-value {
  word-break: break-all;
}

.masked-value {
  color: var(--text-muted);
}

.no-items {
  color: var(--text-muted);
  font-style: italic;
}

/* Form inputs */
.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  font-size: 0.95rem;
  font-family: inherit;
  background-color: rgba(61, 61, 61, 0.3);
  color: var(--text-primary);
  transition: all 0.2s ease;
}

.form-input::placeholder,
.form-textarea::placeholder {
  color: var(--text-muted);
}

.form-input:hover,
.form-select:hover,
.form-textarea:hover {
  border-color: rgba(255, 255, 255, 0.15);
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(60, 98, 230, 0.15);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

/* Status toggle */
.status-toggle {
  cursor: pointer;
  user-select: none;
}

.status-toggle.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.toggle-track {
  position: relative;
  display: flex;
  background-color: rgba(61, 61, 61, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2px;
  width: 160px;
  height: 32px;
  overflow: hidden;
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: calc(50% - 2px);
  height: calc(100% - 4px);
  background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
  border-radius: 16px;
  transition: transform 0.2s ease;
  z-index: 1;
}

.toggle-slider.slider-enabled {
  transform: translateX(0);
}

.toggle-slider.slider-disabled {
  transform: translateX(calc(100% + 2px));
  background: linear-gradient(135deg, var(--error-color) 0%, #a83232 100%);
}

.toggle-option {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  transition: color 0.2s ease;
  z-index: 2;
  position: relative;
}

.toggle-option.active {
  color: white;
}

.toggle-option:not(.active) {
  color: var(--text-muted);
}

/* Status badge */
.status-badge {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-block;
}

.status-badge.enabled {
  background-color: rgba(94, 194, 105, 0.15);
  color: var(--success-color);
}

.status-badge.disabled {
  background-color: rgba(221, 82, 76, 0.15);
  color: var(--error-color);
}

/* Exclude list preview */
.exclude-list-preview {
  cursor: pointer;
  color: var(--accent-color);
  padding: 4px 0;
  transition: color 0.2s;
}

.exclude-list-preview:hover {
  color: #5a7ff0;
}

.click-hint {
  color: var(--text-muted);
  font-size: 0.85rem;
  margin-left: 8px;
}

/* Extensions */
.extensions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.extension-tag {
  display: inline-block;
  padding: 4px 10px;
  background: rgba(60, 98, 230, 0.15);
  color: var(--accent-color);
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
}

/* Chip input */
.chip-input-container {
  width: 100%;
}

.chips-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background-color: rgba(61, 61, 61, 0.3);
  min-height: 46px;
  align-items: center;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
  color: white;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
}

.chip-remove {
  cursor: pointer;
  font-size: 1.2rem;
  line-height: 1;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.chip-remove:hover {
  opacity: 1;
}

.chip-input {
  flex: 1;
  min-width: 150px;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.95rem;
  font-family: inherit;
  padding: 4px;
}

.chip-input::placeholder {
  color: var(--text-muted);
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: inherit;
  transition: all 0.2s ease;
}

.btn svg {
  width: 16px;
  height: 16px;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(60, 98, 230, 0.25);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(60, 98, 230, 0.35);
}

.btn-success {
  background: linear-gradient(135deg, var(--success-color) 0%, #3d9948 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(94, 194, 105, 0.25);
}

.btn-success:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(94, 194, 105, 0.35);
}

.btn-secondary {
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

.btn-danger {
  background: linear-gradient(135deg, var(--error-color) 0%, #a83232 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(221, 82, 76, 0.25);
}

.btn-danger:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(221, 82, 76, 0.35);
}

/* Spinners */
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner-large {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(60, 98, 230, 0.2);
  border-top-color: var(--accent-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Loader overlay */
.loader-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.loader-content {
  text-align: center;
  background: rgba(24, 24, 25, 0.9);
  backdrop-filter: blur(20px);
  padding: 40px 60px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
}

.loader-content .spinner-large {
  margin: 0 auto 20px;
}

.loader-text {
  color: var(--text-primary);
  font-weight: 500;
  font-size: 1rem;
}

/* Toast */
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  z-index: 1000;
  max-width: 400px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  white-space: pre-line;
}

.toast svg {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.toast.error {
  background: linear-gradient(135deg, var(--error-color) 0%, #a83232 100%);
}

.toast.success {
  background: linear-gradient(135deg, var(--success-color) 0%, #3d9948 100%);
}

.toast.warning {
  background: linear-gradient(135deg, var(--warning-color) 0%, #b87d2e 100%);
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: rgba(24, 24, 25, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.close-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--text-muted);
  transition: color 0.2s;
  display: flex;
}

.close-button svg {
  width: 20px;
  height: 20px;
}

.close-button:hover {
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
}

.exclude-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.exclude-item {
  padding: 10px 14px;
  background: rgba(61, 61, 61, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  word-break: break-all;
  font-size: 0.9rem;
}

/* Delete modal */
.delete-modal .modal-body {
  text-align: center;
}

.delete-warning {
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;
  background: rgba(221, 82, 76, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-warning svg {
  width: 28px;
  height: 28px;
  color: var(--error-color);
}

.delete-modal p {
  margin: 0 0 8px;
  color: var(--text-primary);
}

.delete-note {
  color: var(--text-muted) !important;
  font-size: 0.9rem;
}

.delete-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
}

/* Transitions */
.fade-overlay-enter-active,
.fade-overlay-leave-active {
  transition: opacity 0.3s ease;
}

.fade-overlay-enter-from,
.fade-overlay-leave-to {
  opacity: 0;
}

.toast-enter-active {
  animation: slideInRight 0.3s ease-out;
}

.toast-leave-active {
  animation: slideOutRight 0.3s ease-in;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOutRight {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.95);
}

/* Responsive */
@media (max-width: 768px) {
  .domain-detail-page {
    padding: 20px 16px;
  }

  .page-header {
    flex-direction: column;
    gap: 16px;
  }

  .action-buttons {
    width: 100%;
  }

  .action-buttons .btn {
    flex: 1;
  }

  .card-section {
    padding: 20px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .timestamps-grid {
    grid-template-columns: 1fr;
  }

  .toast {
    left: 16px;
    right: 16px;
    bottom: 16px;
    max-width: none;
  }

  .delete-actions {
    flex-direction: column-reverse;
  }

  .delete-actions .btn {
    width: 100%;
  }
}
</style>
